import { t, Trans } from "@lingui/macro";
import React, { useEffect, useState, useTransition } from "react";
import { Select, MenuItem, Box, Typography, Tooltip } from "@mui/material";
import ProductSingle from "@/components/core/product-single";
import { useLingui } from "@lingui/react";
import ListSvg from "../svg/category/list";
import GridSvg from "../svg/category/grid";
import PaginationComponent from "./pagination-component";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import PageLoading from "@/components/core/page-loading";
import Cookies from "js-cookie";
import { ICollectionMeta } from "@/types/general";
import { ISimpleProductItem } from "@/types/product";

const sortOptions = [
  { value: "default", label: <Trans>Default</Trans> },
  { value: "price.asc", label: <Trans>Price (low - high)</Trans> },
  { value: "price.desc", label: <Trans>Price (high - low)</Trans> },
  { value: "name.asc", label: <Trans>Name (A - Z)</Trans> },
  { value: "name.desc", label: <Trans>Name (Z - A)</Trans> },
];

const itemsPerPageOptions = [10, 25, 50, 75, 100];
type GRID_STYLE = "grid" | "list" | string;

const ProductList = ({
  products,
  lang,
  setErrors,
  meta,
  paging = true,
  showLoader = false,
}: {
  products: ISimpleProductItem[];
  lang: string;
  meta: ICollectionMeta;
  setErrors?: (errors: boolean) => void;
  paging?: boolean;
  showLoader?: boolean;
}) => {
  const [mobileGridStyle, setMobileGridStyle] = useState<GRID_STYLE>(
    Cookies.get("mobile-category-display") || "grid",
  );

  useEffect(() => {
    Cookies.set("mobile-category-display", mobileGridStyle);
  }, [mobileGridStyle]);

  const sortOrder = meta
    ? `${meta.sort}${meta.sort === "default" ? "" : "." + meta.order}`
    : "default";

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { push } = useRouter();
  const [loading, startTransition] = useTransition();

  const navigate = (url: string) => {
    startTransition(() => {
      push(url);
    });
  };

  const { i18n } = useLingui();

  const handleItemsPerPageChange = (event: any) => {
    const params = new URLSearchParams(searchParams);

    params.set("page", "1");
    params.set("limit", event.target.value);

    navigate(`${pathname}?${params.toString()}`);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);

    params.set("page", page.toString());

    navigate(`${pathname}?${params.toString()}`);
  };

  const handleSortChange = (event: any) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");

    const spec = event.target.value.split(".");
    if (spec.length === 2) {
      params.set("sort", spec[0]);
      params.set("order", spec[1]);
    } else {
      params.delete("order");
      params.set("sort", event.target.value);
    }
    navigate(`${pathname}?${params.toString()}`);
  };

  return (
    <div className={"relative"}>
      {paging && products.length > 0 && (
        <>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "space-between",
              mt: "30px",
              mb: "20px",
            }}
          >
            <Box
              sx={{
                minWidth: 150,
                marginRight: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                <Trans>Show</Trans>
              </Typography>
              <Select
                value={meta.page_size}
                onChange={handleItemsPerPageChange}
                sx={{
                  borderRadius: "8px",
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                {itemsPerPageOptions.map((number) => (
                  <MenuItem key={number} value={number} sx={{ borderBottom: 0, p: "10px" }}>
                    {number}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              sx={{
                minWidth: 150,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                <Trans>Sort Order</Trans>
              </Typography>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                sx={{
                  width: "315px",
                  borderRadius: "8px",
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                {sortOptions.map((option: any) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ borderBottom: 0, p: "10px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              mt: "15px",
              mb: "20px",
              borderTop: "1px solid #dfdfe0",
              borderBottom: "1px solid #dfdfe0",
              width: "100%",
              py: 1,
            }}
          >
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRight: "1px solid #dfdfe0",
              }}
            >
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                {sortOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ borderBottom: 0, p: "10px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-evenly", width: "50%" }}>
              <Tooltip title={t(i18n)`Grid`} arrow placement="top">
                <Box sx={{ cursor: "pointer" }} onClick={() => setMobileGridStyle("grid")}>
                  <GridSvg active={mobileGridStyle === "grid"} />
                </Box>
              </Tooltip>
              <Tooltip title={t(i18n)`List`} arrow placement="top">
                <Box sx={{ cursor: "pointer" }} onClick={() => setMobileGridStyle("list")}>
                  <ListSvg active={mobileGridStyle === "list"} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            mobileGridStyle === "grid"
              ? {
                  xs: "repeat(auto-fill, minmax(160px, 1fr))",
                  sm: "repeat(auto-fill, minmax(180px, 1fr))",
                  md: "repeat(auto-fill, minmax(240px, 1fr))",
                }
              : { xs: "1fr" },
          columnGap: { xs: "15px", md: "25px" },
          rowGap: "36px",
        }}
      >
        {products?.map((product: any) => (
          <ProductSingle
            lang={lang}
            mobileGridStyle={mobileGridStyle}
            key={product.id}
            item={product}
            setErrors={setErrors}
          />
        ))}
      </Box>
      {paging && products.length > 0 && (
        <PaginationComponent
          persistPage={false}
          triggerScroll={false}
          totalPages={meta.total_pages}
          currentPage={meta.current_page}
          itemsToDisplay={meta.page_size}
          totalItems={meta.total_products}
          setCurrentPage={handlePageChange}
        />
      )}
      {(loading || showLoader) && (
        <PageLoading
          heightFit={true}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: "auto",
            width: "100%",
            background: "rgba(255, 255, 255, .8)",
          }}
        />
      )}
    </div>
  );
};
export default ProductList;
